// src/client/renderer/vfx/tile/ambient-glow-pool.ts
//
// Plan 16, Task 2.1 — Ambient tile glow pool (always-on, per-tile-type colour).
//
// Geometry choice: SphereGeometry(radius=0.28, widthSegments=8, heightSegments=6).
// A sphere is chosen over a disc because the glow sits slightly above the tile
// surface (y offset 0.05) and reads better from any camera angle than a flat disc.
// One shared geometry instance; per-emitter MeshBasicMaterial clones (additive blend).
//
// Pulse curve: opacity = lerp(MIN_OPACITY, MAX_OPACITY, (sin(phase) + 1) / 2)
//   where phase advances at 2π / PERIOD_S radians per second.
//   MIN_OPACITY = 0.3, MAX_OPACITY = 0.85, PERIOD_S = 2.0 seconds.
// This keeps the glow always visible while giving a gentle breathing effect.

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { TileType } from '../../../../shared/board/types';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const SPHERE_RADIUS = 0.28;
const SPHERE_W_SEGS = 8;
const SPHERE_H_SEGS = 6;
const Y_OFFSET = 0.05; // hover slightly above tile surface

const PERIOD_S = 2.0;
const PHASE_RATE = (2 * Math.PI) / PERIOD_S; // radians per second
const MIN_OPACITY = 0.3;
const MAX_OPACITY = 0.85;

// ─────────────────────────────────────────────────────────────────────────────
// AMBIENT_GLOW_COLORS — data-only; pathing=0x000000 sentinel → acquire returns null
// ─────────────────────────────────────────────────────────────────────────────

export const AMBIENT_GLOW_COLORS: Record<TileType, number> = {
  start: 0xffffff,
  gold: 0xffd24a,
  heal: 0x6dd56b,
  hazard: 0xd93636,
  item: 0x6aa9ff,
  soul_anchor: 0xc063ff,
  key_shop: 0x2fc0a0,
  weapons_cache: 0xb25a35,
  mystery_box: 0x5a50c8,
  event: 0xffaa33,
  graveyard: 0x6b6b8a,
  pathing: 0x000000,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface AmbientGlowPoolConfig {
  readonly scene: THREE.Scene;
}

export function createAmbientGlowPool({ scene }: AmbientGlowPoolConfig): TileVfxPool {
  // Shared geometry — disposed once in pool.dispose()
  const sharedGeometry = new THREE.SphereGeometry(SPHERE_RADIUS, SPHERE_W_SEGS, SPHERE_H_SEGS);

  // Per-emitter state: mesh → { material, phase }
  const emitterPhase = new Map<THREE.Object3D, number>();

  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    const tileType = args.tileType;

    // Suppress pathing and undefined tile types
    if (!tileType || tileType === 'pathing') return null;

    const color = AMBIENT_GLOW_COLORS[tileType];

    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: MIN_OPACITY,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.FrontSide,
    });

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.set(args.position.x, args.position.y + Y_OFFSET, args.position.z);

    // Start phase at 0 — opacity will advance from MIN_OPACITY on first update
    emitterPhase.set(mesh, 0);

    return mesh;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    for (const [emitter, phase] of emitterPhase) {
      const newPhase = phase + PHASE_RATE * deltaSeconds;
      emitterPhase.set(emitter, newPhase);

      const t = (Math.sin(newPhase) + 1) / 2; // [0, 1]
      const opacity = MIN_OPACITY + t * (MAX_OPACITY - MIN_OPACITY);
      ((emitter as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = opacity;
    }

    return new Set(emitterPhase.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(_emitter: THREE.Object3D): boolean {
    // Always-on pool — never finished
    return false;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    if (!emitterPhase.has(emitter)) return; // safe no-op for unknown emitters
    scene.remove(emitter);
    emitterPhase.delete(emitter);
    // Dispose the per-emitter material clone
    ((emitter as THREE.Mesh).material as THREE.MeshBasicMaterial).dispose();
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Iterate active emitters and free per-emitter materials (forward-defense §Soft #8)
    for (const emitter of emitterPhase.keys()) {
      scene.remove(emitter);
      ((emitter as THREE.Mesh).material as THREE.MeshBasicMaterial).dispose();
    }
    emitterPhase.clear();

    // Dispose shared geometry exactly once
    sharedGeometry.dispose();
  }

  return { acquire, update, isFinished, release, dispose };
}
